import loadjs from "loadjs";
import axios, { AxiosPromise, AxiosResponse } from 'axios';
import appInfo from '../appInfo';
import { getLocale } from './../locale/index';
import { getReadyFailedHook, getReadySuccessHook, regReadyHook } from "../js-api-tools";
import { AnyObj } from "../monitor/types";

export interface TempObj {
  resolve: (reason?: any) => void;
  reject: (reason?: any) => void;
}
export interface Temp {
  [key: string]: TempObj[];
}

export interface ErrCache {
  [key: string]: boolean;
}

export interface LibVersion {
  [module: string]: [libJsVersion: string, libCssVersion: string],
};

let libVersion: LibVersion | null = null;

let libVersionNotExist = false;

const temp: Temp = {};

const errCache: ErrCache = {};


const generateLibVersion = (versionData: any) => {
  const libVersion: any = {};
  const keys = Object.keys(versionData);
  keys.forEach((element) => {
    const libInfo = element.split('/');
    let project = libInfo[2];
    let libType = libInfo.pop();
    if (!libVersion[project] && project) {
      libVersion[project] = [];
    }

    if (libType?.toLowerCase() === 'lib.js') {
      libVersion[project][0] = versionData[element];
    } else if (libType?.toLowerCase() === 'lib.css') {
      libVersion[project][1] = versionData[element];
    }
  });
  return libVersion;
};

function libVersionReady ({ success, failed } : { success: (data: any) => void;  failed: (data: any) => void }) {
  regReadyHook('weappUtils', 'libVersionReady', { success, failed });
}
function getLibVersion() {
  const libVersionFile = `${window.publicDomainstatic || ''}${window.publicUrlstatic || window.publicUrl || ''}/build/moduleversion.json?v=${(new Date()).getTime()}`;
  axios({ url: libVersionFile }).then(res => {
    if (res?.data) {
      libVersion = generateLibVersion(res.data);
      window['WEAPP_LIB_VERSION_MAP'] = libVersion;
      const successHook = getReadySuccessHook('weappUtils', 'libVersionReady');
      successHook(libVersion);
    } else {
      const failedHook = getReadyFailedHook('weappUtils', 'libVersionReady');
      failedHook(new Error(`Load app failed！`));
    }
  }).catch(error => {
    libVersionNotExist = true;
    const failedHook = getReadyFailedHook('weappUtils', 'libVersionReady');
    failedHook(error);
  });
}

getLibVersion();

// 第一次请求成功前，重复请求挂起
function dispath(app: string, action: 'resolve' | 'reject', result: any) {
  if (temp[app]?.length) {
    while(temp[app].length) {
      temp[app][0][action](result);
      temp[app].shift();
    }
  }
  if (action === 'reject') {
    errCache[app] = true;
  }
}

const appTimer: AnyObj = {};
window.corsImportAppTimer = appTimer;

/**
 * @param app: 加载的项目名
 * @param otherParams 其他属性：@weapp/ckeditor4 需要通过 editorJsType 来分别加载不同的js文件
 */
const corsImport = (app: string, propJson?: string, loadUi: boolean = false, otherParams?: { [key: string]: any }) => new Promise((resolve: (reason?: any) => void, reject: (reason?: any) => void) => {
  if (app === '@weapp/vendor' || app === '@weapp/utils' || (app === '@weapp/ui' && !loadUi)) {
    reject(new Error(`${app} can not be loaded by corsImport`));
    return;
  }
  const {
    name: appName,
    libraryName,
    publicUrl,
    publicDomain,
    buildFolder
  } = appInfo(app);

  // 成功加载过，直接返回
  if (window[libraryName] && !propJson) {
    resolve(window[libraryName]);
    return;
  }
  // 加载失败过，直接返回加载失败
  if (errCache[app]) {
    reject(`${app} failed to load!`);
    return;
  }
  errCache[app] = false;

  const timer: AnyObj = { start: Date.now() };
  appTimer[app] = appTimer[app] || timer;

  const loadOptions = {
    success: () => {
      if (window[libraryName]) {
        timer.libEnd = Date.now();
        timer.manifestTime = timer.libStart - timer.start;
        timer.libTime = timer.libEnd - timer.libStart;
        dispath(app, 'resolve', window[libraryName]);
        resolve(window[libraryName]);
        return;
      } else {
        const err = new Error(`Load app ${app} failed！`);
        dispath(app, 'reject', err);
        reject(err);
        return;
      }
    },
    error: (err: any) => {
      dispath(app, 'reject', err);
      reject(err);
    }
  };

  // 正在加载 js 中直接返回
  // if (loadjs.isDefined(app)) {
  //   loadjs.ready(app, loadOptions);
  //   return;
  // }

  // 正在执行后续代码
  if (temp[app] && !propJson) {
    temp[app].push({
      resolve, reject
    });
    return;
  }
  temp[app] = [];

  const manifest: string = `asset-manifest.json?v=${(new Date()).getTime()}`;
  const manifestUrl: string = `${buildFolder}/${propJson || manifest}`;

  const dispatchReject = (err: any) => {
    dispath(app, 'reject', err);
    reject(err);
  }

  const loadLib = (files: { 'lib.js': string; 'lib.css': string }) => {
    const loadFiles: Array<string> = [];
    if (files['lib.js']) {
      loadFiles.push(`${publicDomain}${publicUrl}${files['lib.js']}`);
    }
    if (files['lib.css']) {
      loadFiles.push(`${publicDomain}${publicUrl}${files['lib.css']}`);
    }
    loadjs(loadFiles, app);
    loadjs.ready(app, loadOptions);
  }

  const manifestLoadLib = (res: AxiosResponse<any>) => {
    if (res.data?.files) {
      const { data } = res;
      // 如果是加载 eb 的 propJson，则直接返回结果即可
      if (propJson) {
        resolve(res);
        return;
      }
      timer.libStart = Date.now();
      const { files } = data;
      const loadFiles: Array<string> = [];
      // @weapp/ckeditor4 单独处理: 需要加载对应的ckeditor_[editorJsType].js文件
      if (app === '@weapp/ckeditor4') {
        const loadFiles: Array<string> = [];
        const fileName = `lib_${otherParams?.editorJsType}.js`;
        if (files[fileName]) {
          loadFiles.push(`${publicDomain}${publicUrl}${files[fileName]}`);
        }
        loadjs(loadFiles, app);
        loadjs.ready(app, loadOptions);
      } else {
        loadLib(files);
      }
    } else {
      dispatchReject(new Error(`Load app ${app} failed！`))
    }
  }

  const loadJsFromLibVersion = () => {
    const [libJsVersion, libCssVersion] = libVersion![appName];
    let loadData: any = {};
    if (libJsVersion) {
      loadData['lib.js'] = `/build/${appName}/static/js/lib.js?v=${libJsVersion}`
    }
    if (libCssVersion) {
      loadData['lib.css'] = `/build/${appName}/static/css/lib.css?v=${libCssVersion}`
    }
    loadLib(loadData);
  }

  if (libVersion && libVersion[appName] && app !=='@weapp/ckeditor4') {
    getLocale(app);
    loadJsFromLibVersion()
  } else if (!libVersionNotExist && app !=='@weapp/ckeditor4') {
    getLocale(app);
    libVersionReady({
      success: (() => {
        if (libVersion && libVersion[appName]) {
          loadJsFromLibVersion();
        } else {
          axios({ url: manifestUrl }).then(res => {
            manifestLoadLib(res);
          }).catch(err => {
            dispatchReject(err);
          });
        }
      }),
      failed: (() => {
        axios({ url: manifestUrl }).then(res => {
          manifestLoadLib(res);
        }).catch(err => {
          dispatchReject(err);
        });
      }),
    });
  } else {
    // 正常加载
    Promise.allSettled([
      axios({ url: manifestUrl }),
      getLocale(app)
    ]).then(([res]) => {
      // 不管多语言加载成功与否都进行模块回调
      if (res.status === 'fulfilled') {
        manifestLoadLib(res.value);
      } else {
        dispatchReject(res.reason);
      }
    }).catch(err => {
      dispatchReject(err);
    });
  }
});

corsImport.temp = temp;

export default corsImport;